import { Component, inject, Inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ProfileService } from '../../../core/services/profile/profile';
import { Profile, UpdatePassword } from '../../../core/models/users.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { FormUtils } from '../../../shared/utils';
import { ToastrService } from 'ngx-toastr';
import { LoadingService } from '../../../core/services/loading/loading';

@Component({
  selector: 'app-change-password',
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatIcon],
  templateUrl: './change-password.html',
  styleUrl: './change-password.css',
})
export class ChangePassword {
  selectedProfile: Profile | null = null;
  showPasswordMatchError = false;
  showPassworError = false;
  errorMsg: string[] | null = null;
  private formBuilder = inject(FormBuilder);

  constructor(
    private perfilService: ProfileService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<ChangePassword>,
    private loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: { selectedProfile: Profile },
  ) {}

  ngOnInit(): void {
    this.perfilService.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });

    this.selectedProfile = this.data.selectedProfile;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  matchPassword: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    let pass = group.get('password')?.value;
    let confirmPass = group.get('confirm_password')?.value;
    return pass === confirmPass ? null : { notSame: true };
  };

  updatePasswordForm = this.formBuilder.group(
    {
      old_password: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(8), this.passwordValidator()]],
      confirm_password: ['', [Validators.required, Validators.minLength(8)]],
    },
    { validators: this.matchPassword },
  );

  updatePassword() {
    this.showPasswordMatchError = true;
    this.showPassworError = true;
    const payload = this.updatePasswordForm.value as UpdatePassword;

    if (this.updatePasswordForm.status === 'VALID') {
      this.loadingService.show(true);
      this.perfilService.updatePasswordProfile(payload).subscribe({
        next: () => {
          this.loadingService.show(false);
          const msg = `La contraseña fue actualizada correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
          this.dialogRef.close();
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }

  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      // Verificar si contiene letras minúsculas, mayúsculas, números y simbolos
      const hasLowercase = /[a-z]/.test(value);
      const hasUppercase = /[A-Z]/.test(value);
      const hasNumber = /[0-9]/.test(value);
      const hasSymbol = /[!@#$%^&*(),.?":{}|<>-]/.test(value);

      // Validar todos los requisitos
      if (!(hasLowercase && hasUppercase && hasNumber && hasSymbol)) {
        return {
          passwordStrength:
            'La contraseña debe contener letras mayúsculas y minúsculas, números y símbolos.',
        };
      }
      // La contraseña es válida
      return null;
    };
  }
}
